import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";

import LocationMappingInfo from "component/YardMapDialog/LocationMappingInfo";
import YardMap from "component/YardMapDialog/YardMap";
import LocationsValidationAlert from "./LocationsValidationAlert";
import DeleteYardMapButton from "./DeleteYardMapButton";
import ManualMapCreator from "./ManualMapCreator";
import { clearErrorEditingYardMap, editYardMap } from "store";
import { cartesianPolygonToLatLng } from "helpers";
import { keys } from "i18n";
import { ENABLE_MANUAL_MAP_DRAWING, EDIT_YARD_MAP_TYPES } from "app-constants";

export default function MapContainer({ yardId }) {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [editData, setEditData] = useState(null);
  const { t } = useTranslation();

  const secrets = useSelector((state) => state.secrets);
  const { label, parkingLocations } = useSelector((state) => state.yardConfigData)[yardId];
  const yardMapData = useSelector((state) => state.yardMapData);
  const permissionsData = useSelector((state) => state.permissionsData);
  const featureFlags = useSelector((state) => state.featureFlags);

  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedLocation) {
      setSelectedLocation(
        yardMapData.yardMap.locations.find(
          ({ locationCode }) => locationCode === selectedLocation.locationCode
        )
      );
    }
  }, [yardMapData.yardMap]);

  const selectLocation = (selectedLocation) => setSelectedLocation(selectedLocation);
  const autoSelectLocationWithChangedCode = (locationCode) => setSelectedLocation({ locationCode });

  const handleEditYardMap = (edits, callback) => dispatch(editYardMap(edits, callback));
  const handleClearErrorEditingYardMap = () => dispatch(clearErrorEditingYardMap());

  const canEdit = !!permissionsData?.permissions?.canEdit;

  const buildEditData = () => {
    const { yardMap } = yardMapData;
    const gps = yardMap.gpsPosition;
    const offset = yardMap.offset || {};
    const origin = {
      latitude: gps.latitude + (offset.latitudeOffset || 0),
      longitude: gps.longitude + (offset.longitudeOffset || 0),
    };
    const initialCenter = [origin.latitude, origin.longitude];
    const initialPolygons = (yardMap.locations || []).map((loc) => ({
      id: uuidv4(),
      locationCode: loc.locationCode,
      locationType: loc.locationType,
      vertices: cartesianPolygonToLatLng(origin, loc.polygon),
    }));
    return { initialCenter, initialPolygons };
  };

  const handleStartEditing = () => {
    setEditData(buildEditData());
  };

  const handleSaveEdit = ({ locations }) => {
    const { yardMap } = yardMapData;
    const existingCodes = new Set((yardMap.locations || []).map((l) => l.locationCode));
    const newCodes = new Set(locations.map((l) => l.locationCode));
    const deleteLocationCodes = [...existingCodes].filter((c) => !newCodes.has(c));

    dispatch(
      editYardMap(
        {
          editType: EDIT_YARD_MAP_TYPES.SAVE_LOCATION_DETAILS,
          yardId,
          version: yardMap.version,
          deleteLocationCodes,
          createOrUpdateLocations: locations,
        },
        () => setEditData(null)
      )
    );
  };

  // Same height as the map defined in styles.scss: 100vh - 270px
  // The 270px offset is to account for TCP header and footer and Yard Designer header
  // If there's a need to change, need to change both together
  const dialogHeight = window.innerHeight - 270;

  if (editData) {
    return (
      <ManualMapCreator
        yardId={yardId}
        onCancel={() => setEditData(null)}
        onSave={handleSaveEdit}
        initialPolygons={editData.initialPolygons}
        initialCenter={editData.initialCenter}
      />
    );
  }

  return (
    <Column>
      <Row alignmentHorizontal="justify">
        <Text type="h200">{t(keys.YARD_MAP_MANAGEMENT)}</Text>
        <Row spacing="small">
          {featureFlags[ENABLE_MANUAL_MAP_DRAWING]?.enabled && (
            <Button onClick={handleStartEditing} disabled={!canEdit} data-testid="edit-map-button">
              {t(keys.EDIT_MAP)}
            </Button>
          )}
          <DeleteYardMapButton yardId={yardId} version={yardMapData.yardMap.version} />
        </Row>
      </Row>

      <LocationsValidationAlert yardId={yardId} />

      <Row widths={["fill", "400px"]} alignmentVertical="top" spacing="none">
        <YardMap
          secrets={secrets}
          dialogHeight={dialogHeight}
          yardLabel={label}
          yardMapData={yardMapData}
          parkingLocations={parkingLocations}
          selectedLocation={selectedLocation}
          selectLocation={selectLocation}
          editYardMap={handleEditYardMap}
        />

        <LocationMappingInfo
          dialogHeight={dialogHeight}
          yardMapData={yardMapData}
          selectedLocation={selectedLocation}
          locations={parkingLocations}
          editYardMap={handleEditYardMap}
          autoSelectLocationWithChangedCode={autoSelectLocationWithChangedCode}
          clearErrorEditingYardMap={handleClearErrorEditingYardMap}
          permissionsData={permissionsData}
        />
      </Row>
    </Column>
  );
}

MapContainer.propTypes = {
  yardId: PropTypes.string.isRequired,
};
